import type { EpcProjectMembershipRow } from '../../../services/epcProjectService';
import { isGovernanceRole } from './team/teamCapabilities';

/** Personal capability for the current user on this project. */
export type ProjectMyCap = 'governance' | 'dept_head' | 'manager' | 'engineer' | 'outsider';

/** The current user's active membership rows on this project. */
export function filterMyActiveMemberships(
  memberships: EpcProjectMembershipRow[],
  myUserId: string | undefined,
): EpcProjectMembershipRow[] {
  if (!myUserId) return [];
  return memberships.filter((m) => m.userId === myUserId && !m.inactive);
}

/**
 * Determine effective capability by scanning ALL of the user's active roles.
 * Callers must only act on the result once memberships have actually resolved
 * (see `myCapResolved` in EpcProjectDetailContext) — an in-flight or failed
 * fetch also yields 'outsider', which must never be shown as a workspace.
 */
export function resolveMyCap(
  memberships: EpcProjectMembershipRow[],
  myUserId: string | undefined,
  isSuperAdmin: boolean,
): ProjectMyCap {
  if (isSuperAdmin) return 'governance';
  const mine = filterMyActiveMemberships(memberships, myUserId);
  if (mine.length === 0) return 'outsider';
  const roles = new Set(mine.map((m) => m.role));
  if ([...roles].some((r) => isGovernanceRole(r))) return 'governance';
  // manager beats department_head: a user with both memberships is treated as DM,
  // not DH. Keeps the wrong-workspace bug from surfacing on data anomalies.
  if (roles.has('manager')) return 'manager';
  if (roles.has('department_head')) return 'dept_head';
  if (roles.has('site_engineer')) return 'engineer';
  return 'outsider';
}
